/* eslint-disable react/prop-types */
/**
 * RestroPulse POS · Sprint 2 Part C · Unsettled Orders Queue
 * ────────────────────────────────────────────────────────────────────
 * A single queue covering TAKEAWAY and DELIVERY orders that have had
 * at least one ticket fired but haven't been settled yet. Reachable
 * from the FloorView top bar as a count badge; opens a slide-in panel
 * over the floor so the cashier can pick an order back up in one tap.
 *
 * Distinct from the token/prep queue used by kitchen displays. That
 * one moves items through queued → preparing → ready. THIS queue is
 * about money owed and blocks day close until every entry lands at
 * SETTLED.
 *
 * Each row shows:
 *   • type badge (TAKEAWAY / DELIVERY)
 *   • token
 *   • guest name + phone (where captured)
 *   • item count
 *   • total (post-tax)
 *   • elapsed time since first fire
 *   • BILL PRINTED flag when applicable
 * Tapping a row calls `onResume(order_id)` which routes the caller
 * back to the billing screen for that exact order, state intact.
 *
 * Filter chips at the top: ALL · TAKEAWAY · DELIVERY.
 */
import React, { useMemo, useState, useEffect } from "react";
import { ShoppingBag, Bike, X, ReceiptText, Clock, Phone, User, Hash, Pause } from "lucide-react";
import { unsettledQueue } from "./posReducer";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const elapsed = (ts) => {
  if (!ts) return "—";
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
};

const TypeChip = ({ type }) => {
  const isTakeaway = type === "TAKEAWAY";
  const Icon = isTakeaway ? ShoppingBag : Bike;
  const bg = isTakeaway ? "bg-amber-50 border-amber-200 text-amber-800"
                        : "bg-sky-50 border-sky-200 text-sky-800";
  return (
    <span className={`inline-flex items-center gap-1 h-6 px-2 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${bg}`}
          data-testid={`pos-queue-type-${type}`}>
      <Icon className="w-3 h-3" />
      {isTakeaway ? "Takeaway" : "Delivery"}
    </span>
  );
};

export function UnsettledQueuePanel({ state, open, onClose, onResume }) {
  // Financial Ground Truth (Feb 2026) · single flat list of every
  // incomplete transaction. No tabs, no filters — the cashier just
  // needs to see what's still owed and tap to resume.
  const items = useMemo(() => unsettledQueue(state), [state]);
  const shown = items;

  // Re-render every 30s so the elapsed labels advance without a tap.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    const iv = setInterval(() => tick((t) => t + 1), 30_000);
    return () => clearInterval(iv);
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end"
         data-testid="pos-unsettled-queue-panel"
         onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <aside
        className="relative w-full max-w-[520px] h-full bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="h-12 shrink-0 border-b border-[color:var(--pos-color-border,#E2E8DF)] px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ReceiptText className="w-4 h-4 text-[color:var(--pos-color-primary-text,#1A2421)]" />
            <h2 className="text-[14px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">Open orders</h2>
            <span className="text-[10px] uppercase tracking-widest text-[color:var(--pos-color-secondary-text,#5C6B64)]"
              data-testid="pos-unsettled-queue-count">
              {items.length} {items.length === 1 ? "order" : "orders"}
            </span>
          </div>
          <button type="button" onClick={onClose}
            data-testid="pos-unsettled-queue-close"
            className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-[color:var(--pos-color-page-bg,#F4F6F2)] text-[color:var(--pos-color-primary-text,#1A2421)]">
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* Rows */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {shown.length === 0 && (
            <div className="text-center text-[12px] text-[color:var(--pos-color-secondary-text,#5C6B64)] py-16"
                 data-testid="pos-unsettled-queue-empty">
              No open orders right now.
            </div>
          )}
          {shown.map((entry) => (
            <button key={entry.order_id} type="button"
              onClick={() => { onResume(entry.order_id); onClose(); }}
              data-testid={`pos-queue-row-${entry.order_id}`}
              className="w-full text-left rounded-lg border border-[color:var(--pos-color-border,#E2E8DF)] bg-white hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)] active:bg-[#F0F0EE] p-3 flex flex-col gap-2 transition">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <TypeChip type={entry.type} />
                  {entry.token && (
                    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums whitespace-nowrap">
                      <Hash className="w-3 h-3 text-[color:var(--pos-color-secondary-text,#5C6B64)]" />{entry.token}
                    </span>
                  )}
                  {entry.held && (
                    <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-sky-50 border border-sky-200 text-sky-800 text-[9px] font-semibold uppercase tracking-wider"
                          data-testid="pos-queue-row-held">
                      <Pause className="w-2.5 h-2.5" /> Held
                    </span>
                  )}
                  {entry.printed && (
                    <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[9px] font-semibold uppercase tracking-wider"
                          data-testid="pos-queue-row-printed">
                      <ReceiptText className="w-2.5 h-2.5" /> Bill printed
                    </span>
                  )}
                </div>
                <div className="text-[14px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums whitespace-nowrap">
                  {inr(entry.total)}
                </div>
              </div>
              <div className="flex items-center justify-between text-[11px] text-[color:var(--pos-color-secondary-text,#5C6B64)]">
                <div className="flex items-center gap-3 min-w-0">
                  {entry.guest_name && (
                    <span className="inline-flex items-center gap-1 truncate max-w-[160px]"
                          title={entry.guest_name}>
                      <User className="w-3 h-3" /> {entry.guest_name}
                    </span>
                  )}
                  {entry.guest_phone && (
                    <span className="inline-flex items-center gap-1 whitespace-nowrap tabular-nums">
                      <Phone className="w-3 h-3" /> {entry.guest_phone}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 whitespace-nowrap">
                  <span className="tabular-nums">
                    {entry.item_count} item{entry.item_count === 1 ? "" : "s"}
                  </span>
                  <span className="inline-flex items-center gap-1 tabular-nums"
                        data-testid={`pos-queue-elapsed-${entry.order_id}`}>
                    <Clock className="w-3 h-3" /> {elapsed(entry.first_fired_at)}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </aside>
    </div>
  );
}

function FilterChip({ label, active, onClick, testid }) {
  return (
    <button type="button" onClick={onClick}
      data-testid={testid}
      className={`h-8 px-3 rounded-full border text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap
        ${active
          ? "bg-[#1A2421] border-[#1A2421] text-white"
          : "bg-white border-[color:var(--pos-color-border,#E2E8DF)] text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]"}`}>
      {label}
    </button>
  );
}

/** ── Day-close blocker ─────────────────────────────────────────────
 * Rendered from the FloorView shift-close flow when the operator tries
 * to close the day while unsettled takeaway/delivery orders remain.
 * Lists each entry by token so the operator can find and settle them.
 */
export function UnsettledDayCloseBlocker({ state, open, onClose, onGoToQueue,
                                             onMoveToDues, livePos }) {
  const items = useMemo(() => unsettledQueue(state), [state]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[85] bg-black/50 flex items-center justify-center p-6"
         data-testid="pos-day-close-blocker">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl p-6 flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <ReceiptText className="w-5 h-5 text-rose-700" />
          {/* Financial Ground Truth (Feb 2026) · consistent terminology:
              this dialog blocks BUSINESS-DAY close (not cashier-shift
              close). A cashier shift can close with open orders — those
              orders re-attach to the next shift on the same day. The
              business day cannot close until every order is settled,
              voided or moved to outstanding dues. */}
          <h2 className="text-[16px] font-semibold text-[color:var(--pos-color-primary-text,#1A2421)]">Cannot close business day</h2>
        </div>
        <p className="text-[13px] text-[#3A4441]">
          {items.length} open transaction{items.length === 1 ? "" : "s"} still to resolve.
          Each one must be <strong>settled</strong>, <strong>voided through its authorised workflow</strong>,
          or <strong>moved to outstanding dues</strong> before the business day can close.
        </p>
        <div className="rounded-lg border border-[color:var(--pos-color-border,#E2E8DF)] max-h-64 overflow-y-auto divide-y divide-[color:var(--pos-color-border,#EEF1EE)]">
          {items.map((e) => (
            <div key={e.order_id} className="flex items-center justify-between gap-2 px-3 py-2 text-[12px]"
                 data-testid={`pos-day-close-blocker-row-${e.order_id}`}>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <TypeChip type={e.type} />
                {e.token && (
                  <span className="inline-flex items-center gap-0.5 font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] tabular-nums">
                    <Hash className="w-3 h-3 text-[color:var(--pos-color-secondary-text,#5C6B64)]" />{e.token}
                  </span>
                )}
                {e.guest_name && (
                  <span className="text-[color:var(--pos-color-secondary-text,#5C6B64)] truncate max-w-[120px]">{e.guest_name}</span>
                )}
                <span className="ml-auto tabular-nums font-semibold text-[color:var(--pos-color-primary-text,#1A2421)] shrink-0">{inr(e.total)}</span>
              </div>
              {/* Move-to-dues only for PRINTED orders (server refuses
                  otherwise). OPEN / KOT_FIRED / HELD go via Resume →
                  Settle or Void. */}
              {e.printed && onMoveToDues && (
                <button type="button"
                  onClick={() => onMoveToDues(e)}
                  data-testid={`pos-day-close-move-to-dues-${e.order_id}`}
                  className="shrink-0 h-8 px-2 rounded border border-amber-500 bg-amber-50 text-amber-900 text-[10px] font-semibold hover:bg-amber-100 whitespace-nowrap">
                  Move to dues
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose}
            data-testid="pos-day-close-blocker-cancel"
            className="h-10 flex-1 rounded-lg border border-[color:var(--pos-color-border,#E2E8DF)] text-[13px] font-medium text-[color:var(--pos-color-secondary-text,#5C6B64)] hover:bg-[color:var(--pos-color-raised-surface,#F9F9F8)]">
            Cancel
          </button>
          <button type="button" onClick={onGoToQueue}
            data-testid="pos-day-close-blocker-open-queue"
            className="h-10 flex-[2] rounded-lg bg-[color:var(--color-primary)] text-white text-[13px] font-semibold hover:brightness-110">
            Resume → settle each
          </button>
        </div>
      </div>
    </div>
  );
}
